/** @jsxImportSource theme-ui */
import React, { useState, useEffect, useCallback } from 'react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import {
  Box,
  Button,
  Text,
  TextInput,
  Card,
  Spinner,
  Flex,
  useToastContext,
} from '@infotrack/zenith-ui';
import { PeriodSelector } from '@/shared/components/periodSelector';
import { AddUpdatePageHeader } from '../components/addUpdatePageHeader';
import { ConfirmDialog } from '@/shared/components/confirmDialog';
import { useStaffList } from '@/features/staff/hooks/useStaffList';
import { useStaffTopFiveData } from '../hooks/useStaffTopFiveData';
import { useAddOrUpdateTopFive } from '../hooks/useAddOrUpdateTopFive';
import { usePeriods } from '../hooks/usePeriods';
import { useITopFiveApiV2EndpointsTopFiveGetTopFiveItemSuggestionsGetTopFiveItemSuggestionsEndpoint } from '@/api/api.generated';
import type { PeriodOption } from '../hooks/usePeriods';
import type {
  ITopFiveApiV2EndpointsTopFiveSharedModelsTopFiveDto,
  ITopFiveApiV2EndpointsStaffSharedModelsStaffItemDto,
} from '@/api/models';
import {
  pageStyles,
  contentStyles,
  headerTextStyles,
  titleStyles,
  subtitleStyles,
  cardStyles,
  successStyles,
  errorStyles,
} from './addUpdateTopFivePage.styles';

interface TopFiveItemForm {
  topFiveItemId: number | null;
  name: string;
  weight: number;
}

interface AddUpdateTopFiveForm {
  staffId: string;
  period: string;
  items: TopFiveItemForm[];
}

const EMPTY_ITEM: TopFiveItemForm = {
  topFiveItemId: null,
  name: '',
  weight: 0,
};

/**
 * Page for adding or updating TopFive records for staff members.
 * 
 * Features:
 * - Search and select staff member
 * - Select current period (Third/Half/Global)
 * - Edit 5 TopFive items with name and weight
 * - Auto-loads existing TopFive if available
 * - Warns before switching periods with unsaved changes
 */
export const AddUpdateTopFivePage: React.FC = () => {
  const toastContext = useToastContext();
  const [selectedStaff, setSelectedStaff] = useState<ITopFiveApiV2EndpointsStaffSharedModelsStaffItemDto | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodOption | null>(null);
  const [currentTopFive, setCurrentTopFive] = useState<ITopFiveApiV2EndpointsTopFiveSharedModelsTopFiveDto | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [staffSearchQuery, setStaffSearchQuery] = useState('');
  const [showStaffDropdown, setShowStaffDropdown] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [activeItemIndex, setActiveItemIndex] = useState<number | null>(null);
  const [itemSearchQueries, setItemSearchQueries] = useState<{ [key: number]: string }>({});
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    onCancel?: () => void;
    variant?: 'warning' | 'danger' | 'info';
    confirmText?: string;
  }>({ isOpen: false, title: '', message: '', onConfirm: () => {} });

  const { staffList, isLoading: isLoadingStaff } = useStaffList();
  const { data: periods, isLoading: isLoadingPeriods } = usePeriods();
  const { topFives, isLoading: isLoadingTopFives } = useStaffTopFiveData({
    staffId: selectedStaff?.staffId ?? null,
    enabled: !!selectedStaff,
  });
  const { addOrUpdate, isLoading: isSaving, isSuccess, isError, error, reset: resetMutation } = useAddOrUpdateTopFive();

  // Get suggestions for TopFive item names
  const currentSearchQuery = activeItemIndex !== null ? itemSearchQueries[activeItemIndex] : null;
  const shouldFetchSuggestions = !!currentSearchQuery && currentSearchQuery.length >= 3;
  
  const { data: suggestionsData, isLoading: isLoadingSuggestions } = useITopFiveApiV2EndpointsTopFiveGetTopFiveItemSuggestionsGetTopFiveItemSuggestionsEndpoint(
    {
      searchText: currentSearchQuery || 'xxx', // Provide fallback to prevent validation error when disabled
      limit: 10,
    },
    {
      query: {
        enabled: shouldFetchSuggestions,
        staleTime: 5 * 60 * 1000, // Cache for 5 minutes
      },
    }
  );

  // Extract item names from suggestions
  const suggestions = React.useMemo(() => {
    return suggestionsData?.data?.suggestions ?? [];
  }, [suggestionsData]);

  const { control, handleSubmit, reset, watch, formState: { isDirty, dirtyFields } } = useForm<AddUpdateTopFiveForm>({
    defaultValues: {
      staffId: '',
      period: '',
      items: Array(5).fill(null).map(() => ({ ...EMPTY_ITEM })),
    },
  });

  const { fields, replace } = useFieldArray({
    control,
    name: 'items',
  });

  const watchedItems = watch('items');
  const enteredItems = watchedItems.filter((i) => i.name.trim());
  const totalWeight = enteredItems.reduce((sum, item) => sum + item.weight, 0);
  const isBalanced = Math.abs(totalWeight - 100) < 0.01;
  const delta = totalWeight - 100;

  // Track unsaved changes
  useEffect(() => {
    // Only track changes if we're not currently loading data
    if (!isLoadingData) {
      setHasUnsavedChanges(isDirty);
    }
  }, [isDirty, isLoadingData]);

  // Filter staff by search query - require at least 2 characters
  const filteredStaff = staffSearchQuery && staffSearchQuery.length >= 2
    ? staffList.filter((s) =>
        `${s.fullName}`.toLowerCase().includes(staffSearchQuery.toLowerCase())
      )
    : [];

  // Handle staff selection
  const handleStaffSelect = (staff: ITopFiveApiV2EndpointsStaffSharedModelsStaffItemDto) => {
    if (hasUnsavedChanges) {
      setConfirmDialog({
        isOpen: true,
        title: 'Unsaved Changes',
        message: 'You have unsaved changes. Are you sure you want to switch staff? Your changes will be lost.',
        variant: 'warning',
        onConfirm: () => {
          setConfirmDialog({ ...confirmDialog, isOpen: false });
          proceedWithStaffSelect(staff);
        },
        onCancel: () => {
          setConfirmDialog({ ...confirmDialog, isOpen: false });
        },
      });
      return;
    }

    proceedWithStaffSelect(staff);
  };

  // Proceed with staff selection after confirmation
  const proceedWithStaffSelect = (staff: ITopFiveApiV2EndpointsStaffSharedModelsStaffItemDto) => {

    setIsLoadingData(true);
    setSelectedStaff(staff);
    setCurrentTopFive(null);
    setHasUnsavedChanges(false);
    setShowStaffDropdown(false);
    setStaffSearchQuery(staff.fullName ?? ''); // Fill search box with selected staff name
    
    // Auto-select period based on staff cadence
    if (periods && staff.cadence) {
      const matchingPeriod = periods.find(
        (p) => p.cadenceType?.toLowerCase() === staff.cadence?.toLowerCase() && p.period === 'Current'
      );
      if (matchingPeriod) {
        setSelectedPeriod(matchingPeriod);
        reset({
          staffId: staff.staffId?.toString() ?? '',
          period: JSON.stringify(matchingPeriod),
          items: Array(5).fill(null).map(() => ({ ...EMPTY_ITEM })),
        }, { keepDirty: false });
      } else {
        setSelectedPeriod(null);
        reset({
          staffId: staff.staffId?.toString() ?? '',
          period: '',
          items: Array(5).fill(null).map(() => ({ ...EMPTY_ITEM })),
        }, { keepDirty: false });
      }
    } else {
      setSelectedPeriod(null);
      reset({
        staffId: staff.staffId?.toString() ?? '',
        period: '',
        items: Array(5).fill(null).map(() => ({ ...EMPTY_ITEM })),
      }, { keepDirty: false });
    }
    resetMutation();
    
    // Reset loading flag after a short delay to allow form updates to complete
    setTimeout(() => {
      setIsLoadingData(false);
    }, 100);
  };

  // Handle period selection
  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'period' && value.period) {
        try {
          const period: PeriodOption = JSON.parse(value.period);
          
          // Only update if the period actually changed
          if (JSON.stringify(selectedPeriod) !== JSON.stringify(period)) {
            setSelectedPeriod(period);
            // Don't call loadTopFiveForPeriod here, let the other useEffect handle it
          }
        } catch (e) {
          console.error('Failed to parse period', e);
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, selectedPeriod]);

  // Load TopFive data when topFives data becomes available and we have a selected period
  useEffect(() => {
    if (selectedPeriod && topFives && !isLoadingTopFives) {
      loadTopFiveForPeriod(selectedPeriod);
    }
  }, [topFives, isLoadingTopFives, selectedPeriod]);

  // Load TopFive items for selected period
  const loadTopFiveForPeriod = (period: PeriodOption) => {
    setIsLoadingData(true);
    
    if (!topFives || topFives.length === 0) {
      // No existing TopFives, keep empty form
      const emptyItems = Array(5).fill(null).map(() => ({ ...EMPTY_ITEM }));
      replace(emptyItems);
      setCurrentTopFive(null);
      
      // Reset form with current values to clear dirty state
      setTimeout(() => {
        reset({
          staffId: selectedStaff?.staffId?.toString() ?? '',
          period: JSON.stringify(period),
          items: emptyItems,
        }, { keepDirty: false });
        setHasUnsavedChanges(false);
        setIsLoadingData(false);
      }, 0);
      return;
    }

    // Find TopFive matching the cadence type from currentPeriod only
    const matchingTopFive = topFives.find((tf) => 
      tf.cadence?.toLowerCase() === period.cadenceType?.toLowerCase()
    );

    if (matchingTopFive && matchingTopFive.topFiveItems) {
      setCurrentTopFive(matchingTopFive);
      
      // Fill form with all items from the matching TopFive
      const formItems: TopFiveItemForm[] = matchingTopFive.topFiveItems.slice(0, 5).map((item: any) => ({
        topFiveItemId: item.topFiveItemId ?? null,
        name: item.name ?? '',
        weight: item.weight ?? 0,
      }));

      // Pad with empty items if less than 5
      while (formItems.length < 5) {
        formItems.push({ ...EMPTY_ITEM });
      }

      replace(formItems);
      
      // Reset form with current values to clear dirty state
      setTimeout(() => {
        reset({
          staffId: selectedStaff?.staffId?.toString() ?? '',
          period: JSON.stringify(period),
          items: formItems,
        }, { keepDirty: false });
        setHasUnsavedChanges(false);
        setIsLoadingData(false);
      }, 0);
    } else {
      // No matching TopFive found
      const emptyItems = Array(5).fill(null).map(() => ({ ...EMPTY_ITEM }));
      replace(emptyItems);
      setCurrentTopFive(null);
      
      // Reset form with current values to clear dirty state
      setTimeout(() => {
        reset({
          staffId: selectedStaff?.staffId?.toString() ?? '',
          period: JSON.stringify(period),
          items: emptyItems,
        }, { keepDirty: false });
        setHasUnsavedChanges(false);
        setIsLoadingData(false);
      }, 0);
    }
  };

  // Handle form submission
  const onSubmit = async (data: AddUpdateTopFiveForm) => {
    if (!selectedStaff || !selectedPeriod) {
      return;
    }

    // Filter out empty items
    const validItems = data.items.filter((item) => item.name.trim() !== '');

    if (validItems.length === 0) {
      setConfirmDialog({
        isOpen: true,
        title: 'No Goals Entered',
        message: 'Please add at least one TopFive item before saving.',
        variant: 'info',
        confirmText: 'OK',
        onConfirm: () => {
          setConfirmDialog({ ...confirmDialog, isOpen: false });
        },
      });
      return;
    }

    // Validate total weight
    const totalWeight = validItems.reduce((sum, item) => sum + item.weight, 0);
    if (Math.abs(totalWeight - 100) > 0.01) {
      setConfirmDialog({
        isOpen: true,
        title: 'Weight Imbalance',
        message: `Total weight is ${totalWeight.toFixed(2)}% (should be 100%). Do you want to continue anyway?`,
        variant: 'warning',
        onConfirm: () => {
          setConfirmDialog({ ...confirmDialog, isOpen: false });
          submitTopFive(validItems);
        },
        onCancel: () => {
          setConfirmDialog({ ...confirmDialog, isOpen: false });
        },
      });
      return;
    }

    submitTopFive(validItems);
  };

  // Submit the TopFive data
  const submitTopFive = async (validItems: TopFiveItemForm[]) => {
    if (!selectedStaff || !selectedPeriod) {
      return;
    }

    try {
      await addOrUpdate({
        staffId: selectedStaff.staffId!,
        request: {
          topFiveId: currentTopFive?.topFiveId ?? null,
          startDate: selectedPeriod.startDate!,
          endDate: selectedPeriod.endDate!,
          items: validItems.map((item) => ({
            topFiveItemId: item.topFiveItemId,
            name: item.name.trim(),
            weight: item.weight,
            completion: null,
            status: null,
          })),
        },
      });

      setHasUnsavedChanges(false);
      toastContext.success({
        title: 'Success',
        message: 'TopFive saved successfully!',
      });
    } catch (err) {
      console.error('Failed to save TopFive', err);
    }
  };

  return (
    <Flex sx={pageStyles} as="main">
      <AddUpdatePageHeader />

      <Box sx={contentStyles} as="section" aria-label="Add/Update Staff TopFive">
        {/* Page Header */}
        <Box sx={headerTextStyles}>
          <Box sx={titleStyles}>
            <Text variant="title" as="h1">Add/Update Staff TopFive</Text>
          </Box>
          <Box sx={subtitleStyles}>
            <Text variant="medium">
              Select a staff member and period to add or update their TopFive goals.
            </Text>
          </Box>
        </Box>

        {isSuccess && (
          <Box sx={successStyles}>
            <Text variant="small">
              TopFive saved successfully! {currentTopFive ? 'Updated' : 'Created'}{' '}
              {watchedItems.filter((i) => i.name.trim()).length} item(s).
            </Text>
          </Box>
        )}

        {isError && error && (
          <Box sx={errorStyles}>
            <Text variant="small">Failed to save TopFive: {error.message}</Text>
          </Box>
          )}

        <form onSubmit={handleSubmit(onSubmit)}>
          <Flex sx={{ gap: 4, flexDirection: ['column', null, 'row'], alignItems: ['stretch', null, 'stretch'] }}>
            {/* Staff Selection */}
            <Box sx={{ flex: 1 }}>
              <Card padding="large" borderRadius="medium" borderColor="neutral/200" boxShadow="medium" sx={{ ...cardStyles, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ marginBottom: 3 }}>
                  <Text variant="title" as="h2">1. Select Staff Member</Text>
                </Box>

                <Box sx={{ marginBottom: 3 }}>
                  <Box sx={{ marginBottom: 2 }}>
                    <Text variant="medium">Search Staff</Text>
                  </Box>
                  <TextInput
                    placeholder="Search by name..."
                    value={staffSearchQuery}
                    onChange={(e) => {
                      setStaffSearchQuery(e.target.value);
                      setShowStaffDropdown(true);
                    }}
                    onFocus={() => setShowStaffDropdown(true)}
                  />
                </Box>

                {showStaffDropdown && (isLoadingStaff ? (
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center',
                    padding: 4,
                    minHeight: '120px',
                  }}>
                    <Spinner />
                  </Box>
                ) : filteredStaff.length > 0 ? (
                  <Box
                    sx={{
                      maxHeight: '380px',
                      overflowY: 'auto',
                      overflowX: 'hidden',
                      border: '1px solid',
                      borderColor: 'rgba(0, 0, 0, 0.08)',
                      borderRadius: '12px',
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(0, 0, 0, 0.04)',
                      backgroundColor: '#ffffff',
                      marginTop: 2,
                      '&::-webkit-scrollbar': {
                        width: '8px',
                      },
                      '&::-webkit-scrollbar-track': {
                        background: 'rgba(0, 0, 0, 0.02)',
                        borderRadius: '12px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: 'rgba(0, 0, 0, 0.15)',
                        borderRadius: '12px',
                        '&:hover': {
                          background: 'rgba(0, 0, 0, 0.25)',
                        },
                      },
                    }}
                  >
                    {filteredStaff.map((staff, index) => (
                      <Box
                        key={staff.staffId}
                        onClick={() => handleStaffSelect(staff)}
                        sx={{
                          padding: '16px 20px',
                          cursor: 'pointer',
                          position: 'relative',
                          backgroundColor:
                            selectedStaff?.staffId === staff.staffId 
                              ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(12, 126, 150, 0.08) 100%)' 
                              : 'transparent',
                          borderBottom: index < filteredStaff.length - 1 ? '1px solid' : 'none',
                          borderBottomColor: 'rgba(0, 0, 0, 0.04)',
                          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            backgroundColor: selectedStaff?.staffId === staff.staffId 
                              ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.12) 0%, rgba(12, 126, 150, 0.12) 100%)'
                              : 'rgba(102, 126, 234, 0.04)',
                            transform: 'translateX(4px)',
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              left: 0,
                              top: 0,
                              bottom: 0,
                              width: '4px',
                              background: 'linear-gradient(135deg, #667eea 0%, #0c7e96 100%)',
                              borderRadius: '0 4px 4px 0',
                            },
                          },
                          '&:first-of-type': {
                            borderTopLeftRadius: '12px',
                            borderTopRightRadius: '12px',
                          },
                          '&:last-of-type': {
                            borderBottomLeftRadius: '12px',
                            borderBottomRightRadius: '12px',
                          },
                        }}
                      >
                        <Flex sx={{ alignItems: 'center', gap: 3 }}>
                          <Box
                            sx={{
                              width: '40px',
                              height: '40px',
                              borderRadius: '50%',
                              background: selectedStaff?.staffId === staff.staffId
                                ? 'linear-gradient(135deg, #667eea 0%, #0c7e96 100%)'
                                : 'linear-gradient(135deg, rgba(102, 126, 234, 0.12) 0%, rgba(12, 126, 150, 0.12) 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: selectedStaff?.staffId === staff.staffId ? '#ffffff' : '#667eea',
                              fontWeight: '600',
                              fontSize: '16px',
                              flexShrink: 0,
                              transition: 'all 0.2s ease',
                              boxShadow: selectedStaff?.staffId === staff.staffId
                                ? '0 4px 12px rgba(102, 126, 234, 0.3)'
                                : '0 2px 8px rgba(102, 126, 234, 0.1)',
                            }}
                          >
                            {staff.fullName?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '?'}
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box as="div" sx={{ 
                              fontWeight: selectedStaff?.staffId === staff.staffId ? '600' : '500',
                              color: selectedStaff?.staffId === staff.staffId ? '#1a202c' : '#2d3748',
                              marginBottom: '4px',
                              fontSize: '15px',
                            }}>
                              {staff.fullName}
                            </Box>
                            <Flex sx={{ gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                              {staff.cadence && (
                                <Box as="span" sx={{ 
                                  color: 'rgba(0, 0, 0, 0.5)',
                                  fontSize: '12px',
                                  fontWeight: '400',
                                }}>
                                  {staff.cadence}
                                </Box>
                              )}
                              {staff.department && (
                                <Box as="span" sx={{ 
                                  color: 'rgba(0, 0, 0, 0.5)',
                                  fontSize: '12px',
                                  fontWeight: '400',
                                }}>
                                  • {staff.department}
                                </Box>
                              )}
                              {staff.staffId && (
                                <Box as="span" sx={{ 
                                  color: 'rgba(0, 0, 0, 0.4)',
                                  fontSize: '11px',
                                  fontWeight: '400',
                                }}>
                                  • ID: {staff.staffId}
                                </Box>
                              )}
                            </Flex>
                            {(staff.managerName || staff.executiveId || staff.isManager) && (
                              <Flex sx={{ gap: 2, marginTop: '6px', flexWrap: 'wrap' }}>
                                {staff.isManager && (
                                  <Box as="span" sx={{ 
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    padding: '2px 8px',
                                    borderRadius: '4px',
                                    backgroundColor: 'rgba(168, 85, 247, 0.08)',
                                    color: '#a855f7',
                                    fontSize: '11px',
                                    fontWeight: '500',
                                  }}>
                                    <Box as="span" sx={{ fontSize: '10px' }}>👔</Box>
                                    Manager
                                  </Box>
                                )}
                                {staff.managerName && (
                                  <Box as="span" sx={{ 
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    padding: '2px 8px',
                                    borderRadius: '4px',
                                    backgroundColor: 'rgba(102, 126, 234, 0.08)',
                                    color: '#667eea',
                                    fontSize: '11px',
                                    fontWeight: '500',
                                  }}>
                                    <Box as="span" sx={{ fontSize: '10px' }}>👤</Box>
                                    Reports to: {staff.managerName}
                                  </Box>
                                )}
                                {staff.executiveId && (
                                  <Box as="span" sx={{ 
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    padding: '2px 8px',
                                    borderRadius: '4px',
                                    backgroundColor: 'rgba(12, 126, 150, 0.08)',
                                    color: '#0c7e96',
                                    fontSize: '11px',
                                    fontWeight: '500',
                                  }}>
                                    <Box as="span" sx={{ fontSize: '10px' }}>⭐</Box>
                                    Executive
                                  </Box>
                                )}
                              </Flex>
                            )}
                          </Box>
                          {selectedStaff?.staffId === staff.staffId && (
                            <Box
                              sx={{
                                width: '24px',
                                height: '24px',
                                borderRadius: '50%',
                                backgroundColor: '#667eea',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontSize: '14px',
                                fontWeight: 'bold',
                                flexShrink: 0,
                              }}
                            >
                              ✓
                            </Box>
                          )}
                        </Flex>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ 
                    padding: 4, 
                    textAlign: 'center',
                    marginTop: 2,
                    borderRadius: '12px',
                    backgroundColor: 'rgba(0, 0, 0, 0.02)',
                    border: '1px dashed',
                    borderColor: 'rgba(0, 0, 0, 0.1)',
                  }}>
                    <Box as="div" sx={{ color: 'rgba(0, 0, 0, 0.5)', fontSize: '14px' }}>
                      No staff members found
                    </Box>
                  </Box>
                ))}

                {selectedStaff && (
                  <Box sx={{ marginTop: 3, padding: 2, backgroundColor: 'info.50', borderRadius: '8px' }}>
                    <Text variant="small">
                      Selected: <strong>{selectedStaff.fullName}</strong>
                    </Text>
                  </Box>
                )}
              </Card>
          </Box>

          {/* Period Selection */}
          <Box sx={{ flex: 1 }}>
            <Card padding="large" borderRadius="medium" borderColor="neutral/200" boxShadow="medium" sx={{ ...cardStyles, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ marginBottom: 3 }}>
                <Text variant="title" as="h2">2. Select Period</Text>
              </Box>

              <PeriodSelector
                name="period"
                control={control}
                label="TopFive Period"
                placeholder="Select a period..."
                currentOnly={true}
                disabled={!selectedStaff}
              />
            </Card>
          </Box>
        </Flex>

        {/* TopFive Items Editor */}
        {selectedStaff && selectedPeriod && (
          <Box sx={{ marginTop: 4 }}>
            <Card padding="large" borderRadius="medium" borderColor="neutral/200" boxShadow="medium" sx={cardStyles}>
              <Box sx={{ marginBottom: 3 }}>
                <Text variant="title" as="h2">3. TopFive Goals</Text>
              </Box>

              {(isLoadingTopFives || isLoadingData) ? (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  justifyContent: 'center', 
                  alignItems: 'center',
                  minHeight: '300px',
                  gap: 2,
                }}>
                  <Spinner />
                  <Box as="div" sx={{ color: 'rgba(0, 0, 0, 0.5)', fontSize: '14px' }}>
                    Loading TopFive data...
                  </Box>
                </Box>
              ) : (
                <>
              <Box sx={{ marginBottom: 3, width: '100%', overflow: 'visible' }}>
                {fields.map((field, index) => (
                  <Flex
                    key={field.id}
                    sx={{
                      gap: 3,
                      marginBottom: 4,
                      alignItems: ['stretch', null, 'center'],
                      flexDirection: ['column', null, 'row'],
                      width: '100%',
                      paddingY: 3,
                      paddingX: 2,
                      borderRadius: '8px',
                      backgroundColor: 'transparent',
                      flexWrap: ['wrap', null, 'nowrap'],
                      transition: 'background-color 0.2s ease',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.02)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #667eea 0%, #0c7e96 100%)',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                        fontSize: '18px',
                        flexShrink: 0,
                        alignSelf: ['flex-start', null, 'center'],
                        boxShadow: '0 4px 6px rgba(102, 126, 234, 0.25)',
                      }}
                    >
                      {index + 1}
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0, width: '100%', position: 'relative' }}>
                      <Controller
                        name={`items.${index}.name`}
                        control={control}
                        render={({ field }) => (
                          <Box>
                            <TextInput
                              {...field}
                              placeholder="Enter TopFive goal description..."
                              onChange={(e) => {
                                field.onChange(e);
                                setItemSearchQueries((prev) => ({ ...prev, [index]: e.target.value }));
                                setActiveItemIndex(index);
                              }}
                              onFocus={() => setActiveItemIndex(index)}
                              onBlur={() => {
                                // Delay to allow clicking on suggestions
                                setTimeout(() => {
                                  if (activeItemIndex === index) {
                                    setActiveItemIndex(null);
                                  }
                                }, 200);
                              }}
                            />
                            {activeItemIndex === index && (currentSearchQuery && currentSearchQuery.length >= 3) && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: '100%',
                                  left: 0,
                                  right: 0,
                                  marginTop: '4px',
                                  maxHeight: '200px',
                                  overflowY: 'auto',
                                  backgroundColor: '#ffffff',
                                  border: '1px solid',
                                  borderColor: 'rgba(0, 0, 0, 0.08)',
                                  borderRadius: '8px',
                                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12), 0 2px 6px rgba(0, 0, 0, 0.04)',
                                  zIndex: 1000,
                                  '&::-webkit-scrollbar': {
                                    width: '6px',
                                  },
                                  '&::-webkit-scrollbar-track': {
                                    background: 'rgba(0, 0, 0, 0.02)',
                                  },
                                  '&::-webkit-scrollbar-thumb': {
                                    background: 'rgba(0, 0, 0, 0.15)',
                                    borderRadius: '3px',
                                  },
                                }}
                              >
                                {isLoadingSuggestions ? (
                                  <Box sx={{ padding: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60px' }}>
                                    <Spinner size="small" />
                                  </Box>
                                ) : suggestions.length > 0 ? (
                                  suggestions.map((suggestion, suggestionIndex) => (
                                    <Box
                                      key={suggestionIndex}
                                      onMouseDown={(e) => {
                                        e.preventDefault(); // Prevent blur
                                        field.onChange(suggestion);
                                        setActiveItemIndex(null);
                                      }}
                                      sx={{
                                        padding: '10px 12px',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        borderBottom: suggestionIndex < suggestions.length - 1 ? '1px solid' : 'none',
                                        borderBottomColor: 'rgba(0, 0, 0, 0.04)',
                                        transition: 'background-color 0.15s ease',
                                        '&:hover': {
                                          backgroundColor: 'rgba(102, 126, 234, 0.06)',
                                        },
                                        '&:first-of-type': {
                                          borderTopLeftRadius: '8px',
                                          borderTopRightRadius: '8px',
                                        },
                                        '&:last-of-type': {
                                          borderBottomLeftRadius: '8px',
                                          borderBottomRightRadius: '8px',
                                        },
                                      }}
                                    >
                                      {suggestion}
                                    </Box>
                                  ))
                                ) : (
                                  <Box sx={{ padding: 3, textAlign: 'center', color: 'rgba(0, 0, 0, 0.5)', fontSize: '13px' }}>
                                    No suggestions found
                                  </Box>
                                )}
                              </Box>
                            )}
                          </Box>
                        )}
                      />
                    </Box>

                    <Box sx={{ width: ['100%', null, '110px'], flexShrink: 0, marginTop: [2, null, 0] }}>
                      <Controller
                        name={`items.${index}.weight`}
                        control={control}
                        render={({ field }) => (
                          <Box sx={{ position: 'relative' }}>
                            <input
                              {...field}
                              type="number"
                              min={0}
                              max={100}
                              step={1}
                              placeholder="0"
                              value={String(field.value)}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => field.onChange(parseFloat(e.target.value) || 0)}
                              style={{
                                width: '100%',
                                padding: '10px 32px 10px 12px',
                                border: '2px solid #e5e7eb',
                                borderRadius: '8px',
                                fontSize: '16px',
                                textAlign: 'center',
                                fontFamily: 'inherit',
                                fontWeight: '600',
                                backgroundColor: '#fff',
                                transition: 'all 0.2s ease',
                              }}
                              onFocus={(e) => {
                                e.target.style.outline = 'none';
                                e.target.style.borderColor = '#667eea';
                                e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                              }}
                              onBlur={(e) => {
                                e.target.style.borderColor = '#e5e7eb';
                                e.target.style.boxShadow = 'none';
                              }}
                            />
                            <Box
                              sx={{
                                position: 'absolute',
                                right: '12px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                pointerEvents: 'none',
                                color: '#9ca3af',
                                fontWeight: '600',
                                fontSize: '16px',
                              }}
                            >
                              %
                            </Box>
                          </Box>
                        )}
                      />
                    </Box>
                  </Flex>
                ))}
              </Box>

              <Flex
                sx={{
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: 4,
                  paddingTop: 3,
                  borderTop: '1px solid',
                  borderTopColor: 'border.subtle',
                  flexDirection: ['column', null, 'row'],
                  gap: 3,
                }}
              >
                <Box>
                  <Box sx={{ marginBottom: 1 }}>
                    <Text variant="medium">Total Weight</Text>
                  </Box>
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 2,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'inline-block',
                        paddingX: 2,
                        paddingY: 1,
                        borderRadius: '9999px',
                        minWidth: '64px',
                        textAlign: 'center',
                        fontWeight: 'bold',
                        border: '1px solid',
                        transition: 'background-color .2s ease, border-color .2s ease, color .2s ease',
                        backgroundColor: isBalanced ? 'success/50' : 'warning/50',
                        borderColor: isBalanced ? 'success/300' : 'warning/300',
                        color: isBalanced ? 'success/700' : 'warning/700',
                      }}
                      role="status"
                      aria-live="polite"
                    >
                      {totalWeight.toFixed(2)}%
                    </Box>
                    <Box as="span" sx={{ color: 'neutral/600' }}>
                      <Text variant="caption">Target 100%</Text>
                    </Box>
                    <Box as="span" sx={{ color: 'neutral/600' }}>
                      <Text variant="caption">
                        {isBalanced ? '✓ Balanced' : `⚠ ${delta > 0 ? '+' : ''}${delta.toFixed(2)}% from target`}
                      </Text>
                    </Box>
                  </Box>
                  <Box sx={{ marginTop: 2 }}>
                    <Text variant="caption">
                      {watchedItems.filter((i) => i.name.trim()).length} goal(s) entered
                    </Text>
                  </Box>
                </Box>

                <Button type="submit" disabled={isSaving || !selectedStaff || !selectedPeriod}>
                  {isSaving ? 'Saving...' : currentTopFive ? 'Update TopFive' : 'Create TopFive'}
                </Button>
              </Flex>
              </>
              )}
            </Card>
          </Box>
        )}
      </form>
      </Box>

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        variant={confirmDialog.variant}
        confirmText={confirmDialog.confirmText || 'Confirm'}
        cancelText="Cancel"
        onConfirm={confirmDialog.onConfirm}
        onCancel={confirmDialog.onCancel}
      />
    </Flex>
  );
};
